/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Breadcrumbs, Grid, Link, Tab, Tabs, Typography, useMediaQuery, Stack, Button, SvgIcon, CircularProgress, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink, useParams } from 'react-router-dom';
import { HomeLine, Save01 } from '../../../assets';
import { CheckCircle, NavigateNext } from '@mui/icons-material';
import { OnboardingWizardTab } from './enums/OnboardingWizardTab';
import OnboardingCompanyDetails from './components/OnboardingCompanyDetails';
import OnboardingContactDetails from './components/OnboardingContactDetails';
import OnboardingDueDiligence from './components/OnboardingDueDiligence';
import OnboardingDeliveryLocations from './components/OnboardingDeliveryLocations';
import OnboardingConfiguration from './components/OnboardingConfiguration';
import OnboardingUsers from './components/OnboardingUsers';
import PageTitle from '../../../components/shared/PageTitle';
import { CompanyDetailsForm } from './types/CompanyDetailsForm';
import {
  ConfigurationModel, ContactDetails, Int32StringKeyValuePair, OnboardingCataloguesModel, OnboardingPageData, RetailerDeliveryLocationModel,
  RetailerDocumentModel, RetailerOnboardingDataModel,
  UserFranchiseList,
} from '../../../app/services/api/ofdClient';
import { client } from '../../../app/services/api/orderManagementClient';
import useNswagClient from '../../../hooks/api/useNswagClient';

import { Section } from '../../../components/ui/Section';
import PageArea from '../../../components/shared/PageArea';
import { uploadFile, approveFile, downloadFile, deleteFile } from './utils/OnboardingDocumentUtils';
import { editDeliveryLocation, deleteDeliveryLocation } from './utils//OnboardingDeliveryLocationsUtils';
import {
  isCompanyDetailsValid, isConfigurationValid, isContactDetailsValid, isDeliveryLocationsValid,
  isDueDiligenceValid,
} from './utils/OnboardingFormValidations';
import { OnboardingStatusEnum } from './enums/OnboardingStatusEnum';
import { countryToContinent } from './utils/CountryCodeToRegionUtils';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import { UserContext } from '../../../components/shared/useUser';

interface ExtendedConfigurationModel extends ConfigurationModel {
  retailerDeliveryMethods?: any[];
}

const initialCompanyDetailsForm: CompanyDetailsForm = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  country: '',
  region: '',
  longitude: undefined,
  latitude: undefined,
  vatnumber: '',
  awrsnumber: '',
  parentCompany: '',
  directors: '',
  eorinumber: '',
  franchiseName: '',
};

const initialContactDetailsForm: ContactDetails = {
  name: '',
  jobTitle: '',
  email: '',
  phoneNumber: '',
  interestReason: '',
  rangeRequirements: '',
};

/**
 * Creates an initial configuration form with empty default values
 * @returns Extended configuration model with empty fields
 */
const createInitialConfigurationForm = (): ExtendedConfigurationModel => {
  const config: ExtendedConfigurationModel = {
    storeId: '',
    containerSizeId: undefined,
    creditLimit: undefined,
    externalIdentifier: '',
    catalogues: [],
    palletSizeId: undefined,
    leadTime: undefined,
    minContainerFillPercentage: undefined,
    orderCategories: [],
    retailerDeliveryMethods: [],
  };
  return config;
};

const initialConfigurationForm = createInitialConfigurationForm();

const OnboardingWizard = () => {
  const { t } = useTranslation('onboardingWizard');  
  const { user, hasPermissionTo, selectedStore } = useContext(UserContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const canViewUsers = false;
  const [activeTab, setActiveTab] = useState<number>(OnboardingWizardTab.COMPANY_DETAILS);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialForm, setInitialForm] = useState<RetailerOnboardingDataModel | undefined>(undefined);
  const [companyDetailsForm, setCompanyDetailsForm] = useState<CompanyDetailsForm>(initialCompanyDetailsForm);
  const [contactDetailsForm, setContactDetailsForm] = useState<ContactDetails>(initialContactDetailsForm);
  const [documents, setDocuments] = useState<RetailerDocumentModel[]>([]);
  const [deliveryLocations, setDeliveryLocations] = useState<RetailerDeliveryLocationModel[]>([]);
  const [configurationForm, setConfigurationForm] = useState<ExtendedConfigurationModel>(initialConfigurationForm);
  const [completedTabs, setCompletedTabs] = useState<{ [key: number]: boolean }>({});
  const [applicationStatus, setApplicationStatus] = useState<OnboardingStatusEnum>(OnboardingStatusEnum.DRAFT);
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
  const { validateStoreID, getRetailerOnboardingById, updateRetailerOnboarding, getOnboardingPageData, downloadOnboardingDocument, onboardingCatalogues } = useNswagClient();
  const [showLeaveModal, setShowLeaveModal] = useState<boolean>(false);
  const [isProgressing, setIsProgressing] = useState<boolean>(false);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [palletSizes, setPalletSizes] = useState<Int32StringKeyValuePair[]>([]);
  const [containerSizes, setContainerSizes] = useState<Int32StringKeyValuePair[]>([]);
  const [validationMessage, setValidationMessage] = useState<string>(t('validation.error'));
  const [catalogues, setCatalogues] = useState<Int32StringKeyValuePair[]>([]);
  const [orderCategories, setOrderCategories] = useState<Int32StringKeyValuePair[]>([]);
  const [showCreateSuccessMessage, setShowCreateSuccessMessage] = useState<boolean>(false);
  const [showUpdateSuccessMessage, setShowUpdateSuccessMessage] = useState<boolean>(false);

  const userFranchiseList: UserFranchiseList = {
    franchiseNames: (user?.franchises?.map(f => f.franchiseName).filter((name): name is string => !!name)) ?? [],
    otherRegions: hasPermissionTo(['RetailersViewOtherRegion']),
  };
  const [shouldShowDeliveryMethods, setShouldShowDeliveryMethods] = useState<boolean>(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (companyDetailsForm.franchiseName && companyDetailsForm.franchiseName.length > 0) {
      getCatalogues();
    }
  }, [companyDetailsForm.franchiseName]);

  useEffect(() => {
    const isMorrisons = companyDetailsForm.franchiseName?.toLowerCase().includes('morrisons') ?? false;
    setShouldShowDeliveryMethods(isMorrisons);
    
    // If switching to Morrisons and it's a new retailer, load delivery methods
    if (isMorrisons && (!id || parseInt(id) === 0) && !configurationForm.retailerDeliveryMethods?.length) {
      loadDeliveryMethods();
    }
    
    // If switching away from Morrisons, clear delivery methods
    if (!isMorrisons && configurationForm.retailerDeliveryMethods?.length) {
      setConfigurationForm(prev => {
        const updated = { ...prev };
        (updated as any).retailerDeliveryMethods = [];
        return updated as ExtendedConfigurationModel;
      });
    }
  }, [companyDetailsForm.franchiseName, id]);

  const loadData = () => {
    setIsLoading(true);
    getOnboardingPageData().then((result: OnboardingPageData) => {
      if (result) {
        setDocuments(result.documentSkeleton!);
        setPalletSizes(result.palletSizes ?? []);
        setContainerSizes(result.containerSizes ?? []);

        if (id && parseInt(id) > 0) {
          loadRetailer();
        }
      }
    }).then(() => {
      setIsLoading(false);
    }).catch((result) => {
      console.error(result);
    });
  };

  const getCatalogues = () => {
    setIsLoading(true);
    setCatalogues([]);
    setOrderCategories([]);

    const selectedFranchise = companyDetailsForm.franchiseName;

    onboardingCatalogues(selectedFranchise).then((result: OnboardingCataloguesModel) => {
      if (result) {
        setCatalogues(result.catalogues ?? []);
        setOrderCategories(result.orderCategories ?? []);

        const existingFranchise = initialForm?.franchiseName;
        const isSameFranchise = selectedFranchise === existingFranchise;

        setConfigurationForm((prevForm) => {
          const updated: ConfigurationModel = {
            ...prevForm,
            catalogues: isSameFranchise ? initialForm?.configuration?.catalogues ?? [] : [],
            orderCategories: isSameFranchise ? initialForm?.configuration?.orderCategories ?? [] : [],
          };
          return updated as ExtendedConfigurationModel;
        });
      }
    }).catch((result) => {
      setValidationMessage(result.message);
      console.error(result);
      setShowErrorAlert(true);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const handleProgress = () => {
    setShowLeaveModal(true);
  };

  const progressRetailer = () => {
    setIsProgressing(true);

    const valid = validateStore();

    if (!valid) {
      setIsProgressing(false);
      return;
    }

    const newBody: RetailerOnboardingDataModel = {
      retailerId: id ? parseInt(id) : undefined,
      progressStage: true,
    };

    updateRetailerOnboarding(newBody).then(() => {
      setShowUpdateSuccessMessage(true);
      loadData();
    }).catch((result) => {
      console.error(result);
      setShowErrorAlert(true);
    }).finally(() => { 
      setIsProgressing(false); 
      setShowLeaveModal(false);
    });
  };

  const loadDeliveryMethods = () => {
    setIsLoading(true);

    const storeNumber = selectedStore?.storeNumber?.toString();
  
    client.getOnboardingDeliveryMethods(storeNumber).then((result) => {
      if (result && result.data && result.data.ShippingMethodList) {
     
      
        const deliveryMethods = result.data.ShippingMethodList.map((method, index) => ({
          name: method.Name,
          shippingMethodId: method.ShippingMethodId,
          charge: 0.00,
          enabled: index === 0,
          companyId: method.CompanyId, 
          shippingModelId: method.ShippingModelId,
          retailerId: 0,
          isDefault: index === 0,
        }));
      
        setConfigurationForm((prevForm) => {
          const updated: ExtendedConfigurationModel = {
            ...prevForm,
            retailerDeliveryMethods: deliveryMethods,
          };
          return updated;
        });
      }
    }).catch((error) => {
      
      setShowErrorAlert(true);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const validateStore = async (): Promise<boolean> => {
    setIsProgressing(true);
    try {
      const result = await validateStoreID(configurationForm.storeId ?? '', parseInt(id!));
    
      if (!result) {
        setValidationMessage(t('validation.storeIdInvalid'));
        setShowErrorAlert(true);
      }

      return result;
    } catch (error) {
      console.error(error);
      setShowErrorAlert(true);
      return false;
    } finally {
      setIsProgressing(false);
      setShowLeaveModal(false);
    }
  };

  const loadRetailer = () => {

    getRetailerOnboardingById(parseInt(id!), userFranchiseList).then((result: RetailerOnboardingDataModel) => {
      if (result) {
        setApplicationStatus(result.statusId as OnboardingStatusEnum);
        setInitialForm(result);
        setCompanyDetailsForm(result);
        setContactDetailsForm(result.contactDetails!);
        setDocuments(result.currentDocuments ?? []);
        setDeliveryLocations(result.deliveryLocations ?? []);
        const configForm: ConfigurationModel = {
          ...result.configuration!,
        };
        (configForm as ExtendedConfigurationModel).retailerDeliveryMethods = [];
        setConfigurationForm(configForm as ExtendedConfigurationModel);

        if (isCompanyDetailsValid(result)) {
          setCompletedTabs((prev) => ({ ...prev, [OnboardingWizardTab.COMPANY_DETAILS]: true }));
        }
        if (isContactDetailsValid(result.contactDetails!)) {
          setCompletedTabs((prev) => ({ ...prev, [OnboardingWizardTab.CONTACT_DETAILS]: true }));
        }
        if (isDueDiligenceValid(result.currentDocuments ?? [])) {
          setCompletedTabs((prev) => ({ ...prev, [OnboardingWizardTab.DUE_DILIGENCE]: true }));
        }
        if (isDeliveryLocationsValid(result.deliveryLocations ?? [])) {
          setCompletedTabs((prev) => ({ ...prev, [OnboardingWizardTab.DELIVERY_LOCATIONS]: true }));
        }
        if (isConfigurationValid(result.configuration!, [])) {
          setCompletedTabs((prev) => ({ ...prev, [OnboardingWizardTab.CONFIGURATION]: true }));
        }
      }
    }).catch((result) => {
      setValidationMessage(t('validation.error'));
      console.error(result);
      setShowErrorAlert(true);
    });
  };

  const getLatitudeLongitude = (addressLine1: string, city: string, region: string, postalCode: string, country: string): Promise<{ latitude?: number; longitude?: number }> => {
    if (!(window.google && window.google.maps)) {
      return Promise.resolve({ latitude: undefined, longitude: undefined });
    }

    const fullAddress = `${addressLine1}, ${city}, ${region}, ${postalCode}, ${country}`;
    const geocoder = new window.google.maps.Geocoder();

    return new Promise((resolve) => {
      geocoder.geocode({ address: fullAddress }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          if (results[0].formatted_address === 'United Kingdom') {
            resolve({ latitude: undefined, longitude: undefined });
          }
          resolve({ latitude: location.lat(), longitude: location.lng() });
        } else {
          console.error('Geocoding failed: ', status);
          resolve({ latitude: undefined, longitude: undefined });
        }
      });
    });
  };

  const normalizeConfigValue = (v: any): number | undefined =>
    v === '' || v === null ? undefined : Number(v);

  const saveData = async () => {
    if (configurationForm.storeId != null && configurationForm.storeId.length > 0) {
      const valid = await validateStore();

      if (!valid) {
        setIsProgressing(false);
        return;
      }
    }

    const { latitude, longitude } = await getLatitudeLongitude(
      companyDetailsForm.addressLine1 ?? '',
      companyDetailsForm.city ?? '',
      companyDetailsForm.region ?? '',
      companyDetailsForm.postalCode ?? '',
      companyDetailsForm.country ?? '',
    );
   
    const contactDetailsObj: ContactDetails = contactDetailsForm;

    const configurationObj: ConfigurationModel = {
      ...configurationForm,
      containerSizeId: normalizeConfigValue(configurationForm.containerSizeId),
      creditLimit: normalizeConfigValue(configurationForm.creditLimit),
      catalogueId: normalizeConfigValue(configurationForm.catalogueId),
      palletSizeId: normalizeConfigValue(configurationForm.palletSizeId),
      leadTime: normalizeConfigValue(configurationForm.leadTime),
      claimPeriod: normalizeConfigValue(configurationForm.claimPeriod ?? 5),
      orderCategories: configurationForm.orderCategories ?? [],
    };

    const newBody: RetailerOnboardingDataModel = {
      retailerId: id ? parseInt(id) : undefined,
      ...companyDetailsForm,
      latitude,
      longitude,
      contactDetails: contactDetailsObj,
      currentDocuments: documents,
      deliveryLocations,
      configuration: configurationObj,
      deliveryMethods: configurationForm.retailerDeliveryMethods ?? [], 
    };

    const valid = validateRequest(newBody);  
    if (!valid) return;

    updateRetailerOnboarding(newBody).then((result) => {
      if (newBody.retailerId && newBody.retailerId > 0) {
        setShowUpdateSuccessMessage(true);
        loadData();
      } else {
        setShowCreateSuccessMessage(true);
        navigate(`/retailers/${result}`);
      }
    }).catch((result) => {
      setValidationMessage(t('validation.error'));
      console.error(result);
      setShowErrorAlert(true);
    });
  };

  const validateRequest = (body: RetailerOnboardingDataModel) => {

    const minFill = body.configuration?.minContainerFillPercentage;
    if (minFill !== undefined && minFill !== null) {
      const minFillNumber = Number(minFill);
      const isPercentageValid =
        !isNaN(minFillNumber) &&
        minFillNumber >= 0 &&
        minFillNumber <= 100;

      if (!isPercentageValid) {
        setValidationMessage(t('validation.minContainerFillInvalid'));
        setShowErrorAlert(true);
        return false;
      }
    }

    const creditLimit = body.configuration?.creditLimit;
    if (creditLimit !== undefined && creditLimit !== null) {
      const creditLimitNumber = Number(creditLimit);
      if (!isNaN(creditLimitNumber) && creditLimitNumber < 0) {
        setValidationMessage(t('validation.creditLimitInvalid'));
        setShowErrorAlert(true);
        return false;
      }
    }

    const leadTime = body.configuration?.leadTime;
    if (leadTime !== undefined && leadTime !== null) {
      const leadTimeNumber = Number(leadTime);
      if (!isNaN(leadTimeNumber) && (leadTimeNumber < 0 || leadTimeNumber > 365)) {
        setValidationMessage(t('validation.leadTimeInvalid'));
        setShowErrorAlert(true);
        return false;
      }
    }

    return true;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const allTabsCompleted = () => {
    const requiredTabs = [
      OnboardingWizardTab.DELIVERY_LOCATIONS,
      OnboardingWizardTab.COMPANY_DETAILS,
      OnboardingWizardTab.CONTACT_DETAILS,
      OnboardingWizardTab.CONFIGURATION,
    ];

    return requiredTabs.every((tab) => completedTabs[tab]);
  };

  const handleUpdateFormData = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: string, formName: string) => {
    if (formName === 'companyDetails') {
      setCompanyDetailsForm({ ...companyDetailsForm, [fieldName]: e.target.value });
    } else if (formName === 'contactDetails') {
      const updated: ContactDetails = {
        ...contactDetailsForm,
        [fieldName]: e.target.value,
      };
      setContactDetailsForm(updated);
    } else if (formName === 'configuration') {
      setConfigurationForm((prevForm) => {
        const updated: ExtendedConfigurationModel = {
          ...prevForm,
          [fieldName]: fieldName === 'orderCategories'
            ? (typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : e.target.value)
            : e.target.value,
        };
        return updated;
      });
    }
    setIsFormDirty(true);
  };

  const GetRegionFromCountryCode = (countryCode: string) => {
    if (!countryCode) return '';
    return countryToContinent[countryCode] || '';
  };

  const updateAddressFields = async (place: any) => {
    const componentMap: Record<string, string> = {};
    place.forEach((component: { types: any[]; long_name: string; short_name: string }) => {
      component.types.forEach((type) => {
        componentMap[type] = component.long_name;
        if (type === 'country') {
          componentMap['country_code'] = component.short_name;
        }
      });
    });

    const addressLine1 = componentMap['street_number']
      ? `${componentMap['street_number']} ${componentMap['route']}`
      : componentMap['route'] || '';
    const addressLine2 =
      componentMap['sublocality'] ||
      componentMap['locality'] ||
      componentMap['neighborhood'] ||
      '';
    const city =
      componentMap['postal_town'] ||
      componentMap['locality'] ||
      componentMap['administrative_area_level_2'] ||
      '';
    const region = GetRegionFromCountryCode(componentMap['country_code']);
    const country = componentMap['country'] || '';
    const postalCode = componentMap['postal_code'] || '';

    setCompanyDetailsForm((prevData: CompanyDetailsForm) => ({
      ...prevData,
      addressLine1,
      addressLine2,
      city,
      region,
      country,
      postalCode,
    }));
  };

  return <PageArea>
    {isProgressing && (
      <Box
        sx={{
          position: 'fixed',
          zIndex: 1301,
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(255,255,255,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={64} />
      </Box>
    )}
    <Box sx={{ mb: 4 }}>
      {CustomBreadCrumbs(() => { return t('title'); })}
    </Box>
    <Box display="flex"
      width="100%"
      height="100%"
      flexDirection="column" >
      <Grid container>
        <Stack width='100%'
          spacing={2}
          direction={isMobile ? 'column' : 'row'}
          sx={{
            justifyContent: 'space-between',
          }}>
          <Box width={isMobile ? '100%' : 'auto'}>
            <PageTitle subTitle={t('subtitle')}>{t('title')}</PageTitle>
          </Box>
          <Box width={isMobile ? '100%' : 'auto'}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}>
            <Button variant='primary'
              size="lg"
              disabled={!hasPermissionTo(['RetailersDetailsSave']) || companyDetailsForm.name?.length === 0 || companyDetailsForm.franchiseName?.length === 0}
              onClick={() => saveData()}
              startIcon={<SvgIcon><Save01 fill='white' /></SvgIcon>}>
              {t('save')}
            </Button>
            {applicationStatus === OnboardingStatusEnum.DRAFT &&
              <Button
                disabled={!allTabsCompleted() || !hasPermissionTo(['RetailersDetailsApprove'])}
                variant='primary'
                size="lg"
                onClick={() => handleProgress()}
                sx={{ ml: 2 }}
              >
                {t('progress')}
              </Button>
            }
          </Box>
        </Stack>
        {isLoading ? (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container
              sx={{ pt: '6px', pb: '12px' }}
              spacing={4}>
              <Grid item
                xs={12}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    '& .MuiTabs-scrollButtons': {
                      display: {
                        lg: 'none',
                      },
                    },
                  }}
                >
                  <Tab
                    label={<Box display="flex"
                      alignItems="center"
                      gap={1}>
                      {t('companyDetails')}
                      {completedTabs[OnboardingWizardTab.COMPANY_DETAILS] && (
                        <CheckCircle color="primary"
                          fontSize="small" />
                      )}
                    </Box>}
                    value={OnboardingWizardTab.COMPANY_DETAILS} />
                  <Tab
                    label={<Box display="flex"
                      alignItems="center"
                      gap={1}>
                      {t('contactDetails')}
                      {completedTabs[OnboardingWizardTab.CONTACT_DETAILS] && (
                        <CheckCircle color="primary"
                          fontSize="small" />
                      )}
                    </Box>}
                    value={OnboardingWizardTab.CONTACT_DETAILS} />
                  <Tab
                    label={<Box display="flex"
                      alignItems="center"
                      gap={1}>
                      {t('dueDiligence')}
                      {completedTabs[OnboardingWizardTab.DUE_DILIGENCE] && (
                        <CheckCircle color="primary"
                          fontSize="small" />
                      )}
                    </Box>}
                    value={OnboardingWizardTab.DUE_DILIGENCE} />
                  <Tab
                    label={<Box display="flex"
                      alignItems="center"
                      gap={1}>
                      {t('deliveryLocations')}
                      {completedTabs[OnboardingWizardTab.DELIVERY_LOCATIONS] && (
                        <CheckCircle color="primary"
                          fontSize="small" />
                      )}
                    </Box>}
                    value={OnboardingWizardTab.DELIVERY_LOCATIONS} />
                  <Tab
                    label={<Box display="flex"
                      alignItems="center"
                      gap={1}>
                      {t('configuration')}
                      {completedTabs[OnboardingWizardTab.CONFIGURATION] && (
                        <CheckCircle color="primary"
                          fontSize="small" />
                      )}
                    </Box>}
                    value={OnboardingWizardTab.CONFIGURATION} />
                  {canViewUsers &&
                    <Tab
                      label={<Box display="flex"
                        alignItems="center"
                        gap={1}>
                        {t('users')}
                        {completedTabs[OnboardingWizardTab.USERS] && (
                          <CheckCircle color="primary"
                            fontSize="small" />
                        )}
                      </Box>}
                      value={OnboardingWizardTab.USERS} />}
                </Tabs>
              </Grid>
            </Grid><Grid item
              xs={12}
              sx={{ mt: '12px' }}>
              <Section>
                {activeTab === OnboardingWizardTab.COMPANY_DETAILS && <OnboardingCompanyDetails
                  userFranchiseList={userFranchiseList}
                  applicationStatus={applicationStatus}
                  formData={companyDetailsForm}
                  updateAddressFields={(e: any) => updateAddressFields(e)}
                  handleChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: string) => handleUpdateFormData(e, fieldName, 'companyDetails')} />}
                {activeTab === OnboardingWizardTab.CONTACT_DETAILS && <OnboardingContactDetails formData={contactDetailsForm}
                  handleChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: string) => handleUpdateFormData(e, fieldName, 'contactDetails')} />}
                {activeTab === OnboardingWizardTab.DUE_DILIGENCE &&
                  <OnboardingDueDiligence formData={documents}
                    setIsFormDirty={(val: boolean) => setIsFormDirty(val)}
                    deleteFile={(document: RetailerDocumentModel) => deleteFile(document, setDocuments)}
                    download={(documentId: number) => downloadFile(documentId, id ? parseInt(id) : 0, downloadOnboardingDocument)}
                    approve={(category: string) => approveFile(category, setDocuments)}
                    upload={(category: string, file: File) => uploadFile(category, file, setDocuments)} />}
                {activeTab === OnboardingWizardTab.DELIVERY_LOCATIONS && <OnboardingDeliveryLocations formData={deliveryLocations}
                  setIsFormDirty={(val: boolean) => setIsFormDirty(val)}
                  handleEditLocation={(location: RetailerDeliveryLocationModel) => setDeliveryLocations((prev) => editDeliveryLocation(prev, location))}
                  handleDeleteLocation={(id: number) => setDeliveryLocations((prev) => deleteDeliveryLocation(prev, id))}
                  isLoading={isLoading} />}
                {activeTab === OnboardingWizardTab.CONFIGURATION && <OnboardingConfiguration formData={configurationForm}
                  orderCategories={orderCategories}
                  applicationStatus={applicationStatus}
                  catalogues={catalogues}
                  franchiseIsSelected={!!companyDetailsForm.franchiseName && companyDetailsForm.franchiseName.length > 0}
                  palletSizes={palletSizes}
                  containerSizes={containerSizes}
                  retailerMethods={configurationForm.retailerDeliveryMethods ?? []}
                  showDeliveryMethods={shouldShowDeliveryMethods}
                  retailerId={id ? parseInt(id) : undefined}
                  handleChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: string) => handleUpdateFormData(e, fieldName, 'configuration')} />}
                {activeTab === OnboardingWizardTab.USERS && <OnboardingUsers />}
              </Section>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
    <MessagesSnackbar
      open={showErrorAlert}
      onClose={() => setShowErrorAlert(false)}
      message={validationMessage}
      severity="error"
      duration={5000}
    />  
    <MessagesSnackbar
      open={showCreateSuccessMessage}
      onClose={() => setShowCreateSuccessMessage(false)}
      message={t('retailerCreatedMessage')}
      severity="success"
      duration={5000}
    />
    <MessagesSnackbar
      open={showUpdateSuccessMessage}
      onClose={() => setShowUpdateSuccessMessage(false)}
      message={t('retailerUpdatedMessage')}
      severity="success"
      duration={5000}
    />
    <ConfirmationDialog
      variant='primary'
      iconColor={theme.palette.primary}
      iconBgColor={theme.palette.custom.gray}
      open={showLeaveModal}
      onCancel={() => setShowLeaveModal(false)}
      onConfirm={progressRetailer}
      icon={<Save01 />}
      title={t('progress')}
      message={t('progressConfirmMessage')}
      confirmButtonText={t('progressConfirm')}
      cancelButtonText={t('progressCancel')}
      isDisabled={isProgressing}
    />
  </PageArea>;
};

const CustomBreadCrumbs = (getTitle: () => string) => {
  return <Breadcrumbs separator={<NavigateNext fontSize="small" />}
    sx={{ mb: '8px' }}>
    <Link to="/"
      component={RouterLink}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
      }}>
        <HomeLine
          height={'20px'}
          width={'20px'} />
      </Box>
    </Link>
    <Link to="/retailers"
      component={RouterLink}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
      }}>
        <Typography>{'Retailers'}</Typography>
      </Box>
    </Link>
    <Typography>{getTitle()}</Typography>
  </Breadcrumbs>;
};

export default OnboardingWizard;